import React, { useState, useEffect, useCallback } from "react";
import { Calendar, Clock, MapPin, ChevronDown, ChevronUp, Star, RefreshCw } from "lucide-react";

interface ProgrammeEvent {
  id: string;
  title: string;
  category: string;
  startTime: string;
  endTime: string;
  venue: string;
  area?: string;
  description?: string;
  speaker?: string;
  dresscode?: string;
  isHighlight?: boolean;
}

interface ProgrammeDay {
  id: string;
  date: string;
  dayLabel: string;
  theme: string;
  events: ProgrammeEvent[];
}

const CAT_COLORS: Record<string, string> = {
  "Opening Ceremony": "#C9A84C",
  "Cultural Display": "#a78bfa",
  "Sports": "#60a5fa",
  "Religious": "#34d399",
  "Entertainment": "#fb923c",
  "Awards": "#C9A84C",
  "Parade": "#2dd4bf",
  "Community Meeting": "#94a3b8",
  "Gala/Dinner": "#f472b6",
  "Youth Event": "#4ade80",
  "Children Event": "#fbbf24",
  "Security Briefing": "#ef4444",
  "general": "#60a5fa",
  "Other": "#94a3b8"
};

function normalizeEvent(e: any): ProgrammeEvent {
  return {
    id:          e.id || e.eventId || Math.random().toString(36).slice(2),
    title:       e.title || 'Untitled Event',
    category:    e.category || e.type || 'Other',
    startTime:   e.startTime || e.time || '',
    endTime:     e.endTime   || '',
    venue:       e.venue     || e.location || '',
    area:        e.area      || '',
    description: e.description || '',
    speaker:     e.speaker   || '',
    dresscode:   e.dresscode || '',
    isHighlight: e.isHighlight || false,
  };
}

export function FestivalProgramme({ key: _k }: { key?: React.Key }) {
  const [days, setDays]             = useState<ProgrammeDay[]>([]);
  const [loading, setLoading]       = useState(true);
  const [activeDay, setActiveDay]   = useState(0);
  const [expandedEvt, setExpandedEvt] = useState<string | null>(null);
  const [error, setError]           = useState('');

  const loadProgramme = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch("/api/programme");
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      const raw: any[] = Array.isArray(d) ? d
        : Array.isArray(d.days)      ? d.days
        : Array.isArray(d.programme) ? d.programme
        : [];
      const arr: ProgrammeDay[] = raw.map(day => ({
        id:       day.id || day.dayId || '',
        date:     day.date || '',
        dayLabel: day.dayLabel || day.label || '',
        theme:    day.theme || '',
        events:   (Array.isArray(day.events) ? day.events : [])
                    .map(normalizeEvent)
                    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')),
      }));
      setDays(arr);
    } catch(e: any) {
      setError(e.message);
      setDays([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadProgramme(); }, [loadProgramme]);

  const totalEvents = days.reduce((s, d) => s + (d.events?.length || 0), 0);
  const currentDay  = days[activeDay];

  const C = {
    bg:'#070D0A', surface:'#0E1812', card:'#121E16', border:'#1C2E22',
    text:'#EDE8DC', muted:'rgba(237,232,220,0.55)', dim:'rgba(237,232,220,0.35)',
    gold:'#C9A84C',
  };

  if (loading) return (
    <div style={{background:C.bg,minHeight:'100vh',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:'Plus Jakarta Sans, sans-serif'}}>
      <div style={{textAlign:'center',color:C.dim}}>
        <Calendar size={48} style={{margin:'0 auto 16px',opacity:0.3}}/>
        <p style={{margin:0}}>Loading Programme...</p>
      </div>
    </div>
  );

  if (totalEvents === 0) return (
    <div style={{background:C.bg,minHeight:'100vh',padding:'40px 24px',fontFamily:'Plus Jakarta Sans, sans-serif'}}>
      <div style={{maxWidth:700,margin:'0 auto',textAlign:'center',paddingTop:80}}>
        <div style={{width:80,height:80,borderRadius:'50%',background:C.surface,border:`1px solid ${C.border}`,display:'flex',alignItems:'center',justifyContent:'center',margin:'0 auto 24px'}}>
          <Calendar size={36} style={{color:'rgba(201,168,76,0.4)'}}/>
        </div>
        <h2 style={{fontFamily:'Cinzel,serif',color:'rgba(201,168,76,0.7)',fontSize:24,margin:'0 0 12px'}}>
          Programme Coming Soon
        </h2>
        <p style={{color:C.muted,fontSize:15,lineHeight:1.7,margin:'0 0 8px'}}>
          The full festival programme for <strong style={{color:C.gold}}>ERUE Festival of Uzairue Kingdom 2026</strong> is being finalised by the Local Organising Committee.
        </p>
        <p style={{color:C.dim,fontSize:14,margin:'0 0 4px'}}>
          Festival dates: December 24–30, 2026 &nbsp;·&nbsp; Grand Finale: Wednesday, 30 December 2026
        </p>
        {error && <p style={{color:'#ef4444',fontSize:13,marginTop:12}}>Error: {error}</p>}
        <button onClick={loadProgramme} style={{marginTop:24,padding:'10px 24px',background:'rgba(201,168,76,0.1)',border:'1px solid rgba(201,168,76,0.3)',borderRadius:8,color:C.gold,fontSize:13,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:8}}>
          <RefreshCw size={14}/> Retry
        </button>
        <p style={{color:'rgba(237,232,220,0.25)',fontSize:13,marginTop:24,fontStyle:'italic'}}>
          "Uzairue Heritage: Culture, Security, Prosperity"
        </p>
      </div>
    </div>
  );

  return (
    <div style={{background:C.bg,minHeight:'100vh',padding:'24px',fontFamily:'Plus Jakarta Sans, sans-serif'}}>
      <div style={{maxWidth:920,margin:'0 auto'}}>

        {/* Header */}
        <div style={{textAlign:'center',marginBottom:32}}>
          <p style={{color:C.gold,fontSize:11,letterSpacing:4,textTransform:'uppercase',margin:'0 0 8px'}}>
            ERUE Festival of Uzairue Kingdom
          </p>
          <h1 style={{fontFamily:'Cinzel,serif',color:C.gold,fontSize:28,margin:'0 0 8px'}}>
            Festival Programme 2026
          </h1>
          <p style={{color:C.muted,fontSize:14,margin:0}}>
            December 24–30, 2026 &nbsp;·&nbsp; Palace of the Ogieneni, Jattu-Uzairue &nbsp;·&nbsp; {totalEvents} event{totalEvents!==1?'s':''}
          </p>
          <p style={{color:C.dim,fontSize:13,margin:'4px 0 0',fontStyle:'italic'}}>
            "Uzairue Heritage: Culture, Security, Prosperity"
          </p>
          <button onClick={loadProgramme} style={{marginTop:12,padding:'6px 16px',background:'transparent',border:`1px solid ${C.border}`,borderRadius:6,color:C.dim,fontSize:12,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6}}>
            <RefreshCw size={12}/> Refresh
          </button>
        </div>

        {/* Day Tabs */}
        <div style={{display:'flex',gap:6,overflowX:'auto',marginBottom:24,paddingBottom:4}}>
          {days.map((day, i) => {
            const evtCount = day.events?.length || 0;
            const active   = i === activeDay;
            const hasHL    = day.events?.some(e => e.isHighlight);
            return (
              <button key={day.id || i} onClick={() => { setActiveDay(i); setExpandedEvt(null); }} style={{
                flexShrink:0, padding:'12px 18px', borderRadius:10,
                border:`1px solid ${active?C.gold:C.border}`,
                background:active?'#0F5132':C.surface,
                cursor:'pointer', textAlign:'center', transition:'all 0.2s',
              }}>
                <div style={{color:active?C.gold:'rgba(237,232,220,0.6)',fontSize:12,fontWeight:700,fontFamily:'Cinzel,serif'}}>
                  {day.date
                    ? new Date(day.date + 'T12:00:00Z').toLocaleDateString('en-GB',{weekday:'short',month:'short',day:'numeric'})
                    : day.dayLabel || `Day ${i+1}`}
                </div>
                <div style={{color:active?C.text:'rgba(237,232,220,0.4)',fontSize:11,marginTop:3}}>
                  {evtCount} event{evtCount!==1?'s':''} {hasHL?'★':''}
                </div>
              </button>
            );
          })}
        </div>

        {currentDay && (
          <>
            {/* Day Theme Banner */}
            <div style={{background:'linear-gradient(135deg,#0E1812,#121E16)',border:'1px solid rgba(201,168,76,0.2)',borderLeft:`4px solid ${C.gold}`,borderRadius:12,padding:'20px 24px',marginBottom:20}}>
              <h2 style={{color:C.text,fontFamily:'Cinzel,serif',margin:'0 0 4px',fontSize:18}}>{currentDay.dayLabel}</h2>
              {currentDay.theme && <p style={{color:C.gold,fontSize:14,margin:0,fontStyle:'italic'}}>{currentDay.theme}</p>}
            </div>

            {/* Events List */}
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {currentDay.events.length === 0 && (
                <div style={{textAlign:'center',padding:'48px 0',color:C.dim,fontSize:14}}>
                  No events scheduled for this day yet.
                </div>
              )}
              {currentDay.events.map(evt => {
                const color  = CAT_COLORS[evt.category] || '#94a3b8';
                const isOpen = expandedEvt === evt.id;
                return (
                  <div key={evt.id} style={{background:C.surface,border:`1px solid ${isOpen?'rgba(201,168,76,0.3)':C.border}`,borderLeft:`4px solid ${color}`,borderRadius:10,overflow:'hidden',transition:'border-color 0.2s'}}>
                    <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',cursor:'pointer'}} onClick={() => setExpandedEvt(isOpen ? null : evt.id)}>
                      {/* Time block */}
                      <div style={{flexShrink:0,textAlign:'center',minWidth:58,background:C.card,borderRadius:8,padding:'6px 10px'}}>
                        <div style={{color:C.gold,fontSize:14,fontWeight:700}}>{evt.startTime||'TBD'}</div>
                        {evt.endTime && <div style={{color:C.dim,fontSize:11}}>{evt.endTime}</div>}
                      </div>
                      {/* Info */}
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          {evt.isHighlight && <Star size={14} fill={C.gold} style={{color:C.gold,flexShrink:0}}/>}
                          <span style={{color:C.text,fontSize:15,fontWeight:600}}>{evt.title}</span>
                          <span style={{padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,background:color+'22',color,border:`1px solid ${color}44`,flexShrink:0}}>
                            {evt.category}
                          </span>
                        </div>
                        {evt.venue && (
                          <div style={{color:'rgba(237,232,220,0.45)',fontSize:12,marginTop:4,display:'flex',alignItems:'center',gap:4}}>
                            <MapPin size={11}/> {evt.venue}{evt.area?` — ${evt.area}`:''}
                          </div>
                        )}
                      </div>
                      {isOpen
                        ? <ChevronUp size={16} style={{color:C.dim,flexShrink:0}}/>
                        : <ChevronDown size={16} style={{color:C.dim,flexShrink:0}}/>}
                    </div>
                    {isOpen && (
                      <div style={{padding:'0 18px 16px 90px',borderTop:`1px solid ${C.border}`}}>
                        {evt.speaker    && <p style={{color:'rgba(237,232,220,0.7)',fontSize:13,margin:'12px 0 4px'}}><strong style={{color:C.gold}}>Speaker / Performer:</strong> {evt.speaker}</p>}
                        {evt.dresscode  && <p style={{color:'rgba(237,232,220,0.7)',fontSize:13,margin:'4px 0'}}><strong style={{color:C.gold}}>Dress Code:</strong> {evt.dresscode}</p>}
                        {evt.description && <p style={{color:'rgba(237,232,220,0.6)',fontSize:13,margin:'8px 0 0',lineHeight:1.6}}>{evt.description}</p>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Footer */}
        <div style={{textAlign:'center',marginTop:48,paddingTop:24,borderTop:`1px solid ${C.border}`}}>
          <p style={{color:'rgba(237,232,220,0.3)',fontSize:12}}>
            ERUE Festival of Uzairue Kingdom 2026 &nbsp;·&nbsp; Etsako West LGA, Edo State, Nigeria
          </p>
          <p style={{color:'rgba(237,232,220,0.2)',fontSize:11,margin:'4px 0 0'}}>
            "One People, One Kingdom, One Destiny"
          </p>
        </div>
      </div>
    </div>
  );
}

export default FestivalProgramme;
